import type { EscrowTransaction } from "../services/projects-api.service";

/**
 * Said beside a negative escrow balance. `getProjectPayments` in the backend
 * derives the balance as funded - spent - refunded and does not clamp it, so a
 * negative figure means more has been paid out than the homeowner funded.
 * Shared by the homeowner dashboard's budget card, the homeowner overview's
 * IN ESCROW card and the designer Payments tab.
 */
export const OVERDRAWN_ESCROW_NOTE = "Paid out beyond what was funded";

/** The title the designer Payments tab gives a contractor assignment row. */
export const CONTRACTOR_PAYOUT_TITLE = "Contractor payout";

/**
 * The status label for a contractor assignment still at `held`. Its money is
 * part of the funded escrow already counted as held, not a second deposit, so
 * it is labelled as a payout not yet made rather than as money held.
 */
export const AWAITING_PAYOUT_LABEL = "Awaiting payout";

/** The title the homeowner Costs tab gives a top-up row. */
export const FUNDS_ADDED_TITLE = "Funds added";

/**
 * The status label for a top-up at `held`. The money is in escrow and no
 * release of the homeowner's pays it to anyone: a contractor hired from it is
 * paid by `authorizeAssignmentPayment`, from their own assignment row.
 */
export const TOP_UP_HELD_LABEL = "In escrow";

/**
 * The status label for a milestone's funding row a dispute resolution's split
 * has paid the designer part of, while the refund to the homeowner from it has
 * not completed. The backend's payout leg records that part as the row's
 * `releasedAmount` and leaves the row `held` (only a split's payout does both),
 * and moves it to `partially_refunded` once the refund is confirmed. Until
 * then "Awaiting your release" and "In Escrow" are both false of it: part of
 * the money has left escrow, and no release of the homeowner's moves the rest.
 */
export const SPLIT_PART_PAID_LABEL = "Part paid to designer";

/** Whether `txn` is the row `SPLIT_PART_PAID_LABEL` describes. */
export function isSplitPartPaid(
	txn: Pick<EscrowTransaction, "status" | "releasedAmount">,
): boolean {
	return txn.status === "held" && (txn.releasedAmount ?? 0) > 0;
}

/**
 * What the homeowner is told about the refund from a row `isSplitPartPaid`
 * describes, by where the backend records that refund
 * (`disputeRefundState`). A refund recorded `failed` is for support: the
 * backend refuses to retry it or to cancel its resolution, so the homeowner is
 * sent there. One recorded `unconfirmed` is not: an admin's retry of the
 * resolution looks the refund up at the payment provider before sending
 * anything, and records what it finds. Any other state, or none, is still to
 * complete.
 */
export function splitRefundNote(
	txn: Pick<EscrowTransaction, "disputeRefundState">,
): string {
	switch (txn.disputeRefundState) {
		case "failed":
			return "The refund to you from it has run into a problem that our support team must resolve. Please contact support.";
		case "unconfirmed":
			return "The status of the refund to you from it is still being confirmed with the payment provider.";
		default:
			return "The refund to you from it is not complete yet.";
	}
}

/**
 * The status label for a milestone's funding row whose dispute resolution has
 * its payout to the designer recorded `failed` (`disputePayoutState`): the
 * payment provider reported that payout failed, reversed, refused or made for
 * another amount. The backend sends no payout of the row again and refuses to
 * cancel the resolution, so it stays in progress until support settles it.
 * Takes the place of `SPLIT_PART_PAID_LABEL` and of every `held` label.
 */
export const DISPUTE_PAYOUT_FAILED_LABEL = "Needs support";

/** What the homeowner is told about a row `DISPUTE_PAYOUT_FAILED_LABEL` labels. */
export const DISPUTE_PAYOUT_FAILED_NOTE =
	"A dispute resolution's payout to your designer from this payment has run into a problem that our support team must resolve. Please contact support.";

/**
 * Whether `txn` is a duplicate charge: a second payment for a milestone another
 * payment already funded. The backend marks it with `metadata.duplicateCharge`
 * (its `isDuplicateCharge`), never pays it out, and refunds it through an
 * admin dispute.
 */
export function isDuplicateCharge(
	txn: Pick<EscrowTransaction, "metadata">,
): boolean {
	const metadata = txn.metadata as Record<string, unknown> | null | undefined;
	return metadata?.duplicateCharge != null;
}

/** The homeowner's label for a held row `isDuplicateCharge` accepts. */
export const DUPLICATE_CHARGE_LABEL = "Duplicate charge";

/**
 * What the homeowner is told about a duplicate charge that has been refunded.
 * The row keeps its duplicate marker after the refund, so the status label
 * ("Refunded") stays and this note explains it.
 */
export const DUPLICATE_CHARGE_REFUNDED_NOTE =
	"A second payment for a milestone you had already paid for, refunded to you.";

/**
 * What the homeowner is told about a duplicate charge. It is never released to
 * the designer; only a Finishd admin can refund it, through a dispute resolved
 * with a full refund, so the remedy is to contact support.
 */
export const DUPLICATE_CHARGE_NOTE =
	"A second payment for a milestone you had already paid for. It will not be paid to your designer. Contact Finishd support to have it refunded.";

/** Whether `txn` is the row `DISPUTE_PAYOUT_FAILED_LABEL` describes. */
export function isDisputePayoutFailed(
	txn: Pick<EscrowTransaction, "status" | "disputePayoutState">,
): boolean {
	return txn.status === "held" && txn.disputePayoutState === "failed";
}

/**
 * Whether an escrow row is a top-up, the row `topUpEscrow` in the backend's
 * `payments.service.ts` writes: it stamps `metadata.type: "top_up"`.
 *
 * Read from `metadata.type`, as the backend's `isEscrowTopUp` reads it, never
 * from a missing milestone: a contractor assignment and a row whose milestone
 * was deleted carry none either.
 */
export function isEscrowTopUp(
	txn: Pick<EscrowTransaction, "metadata">,
): boolean {
	const { metadata } = txn;
	return metadata != null && "type" in metadata && metadata.type === "top_up";
}

/**
 * Whether an escrow row is the contractor assignment `hireContractor` in the
 * backend's `project-contractors.service.ts` writes: it stamps
 * `metadata.type: "contractor_assignment"` on the row.
 */
export function isContractorAssignment(
	txn: Pick<EscrowTransaction, "metadata">,
): boolean {
	const { metadata } = txn;
	return (
		metadata != null &&
		"type" in metadata &&
		metadata.type === "contractor_assignment"
	);
}
